'use client'
// LOCKED-SCREEN: the unified access-blocked view every surface renders in
// place of the main message content (replaces the per-surface null branches
// and one-off protected boxes).
//
// SKELETON component: it only derives the notice data from the store — the
// access grant plus the delivered main identity (code_key / name_key /
// subject survive a locked load; body and counts are withheld server-side) —
// and renders the package-owned look through the universal content-display
// registry (app/richEdit, kind 'locked'). Surfaces theme it through the
// --content-locked-* CSS vars on any wrapper and their own frame spec.
//
// The pin form inside the look is a plain POST to the unlock route: the pin
// is stored in the shared PHP session and the desk re-boots on the clean URL
// (pins never ride the URL — LOCKED-SCREEN task).

import { useSubsStore } from '@/state/MsgTreeProvider'
import { selectAccessGrant, selectMainState } from '@/msgdeskRuntime/state'
import { ContentView, lockedNoticeDocument, type ContentFrameSpec } from '@/app/richEdit/content'
import { blockedPresentationFromGrant } from './blockedPresentation'
import { LockedEmailLogin } from '@/app/visitor/ui/LockedEmailLogin'
// (colocated: the whole blocking model lives in deskUi/Blocking/)

export const UNLOCK_ACTION = '/api/msgdesk/unlock'

// BLOCKED-PRESENTATION: the main-msg presentation of the blocking model.
// The shared helper is the only decider (enum + phrasing + time side from
// grant.time_side); this component only maps it into the package-owned
// locked document look. Sub rows consume the same state through the
// BlockedPreview text leaf while retaining their registered row variant.

export function LockedScreen({ className, frame }: { className?: string; frame?: ContentFrameSpec }) {
  const { state } = useSubsStore()
  const grant = selectAccessGrant(state)
  const main = selectMainState(state)

  const presentation = blockedPresentationFromGrant(grant)
  if (presentation.blocked === 'none') return null

  const timeLocked = presentation.pinKind === 'edit-only'
  const codeKey = main.codeKey || ''
  const doc = lockedNoticeDocument({
    codeKey,
    nameKey: main.nameKey || '',
    subject: main.subject || '',
    reason: timeLocked ? 'time' : 'show-pin',
    timeInfo: timeLocked ? presentation.text : '',
    unlockAction: UNLOCK_ACTION,
  })

  return (
    <>
      <ContentView doc={doc} className={className} frame={frame} />
      {/* LOCKED-SCREEN email login: the interactive "sign in with this
          message's email" alternative below the static password form. Client
          component (JS-only enhancement); the password form stays no-JS. The
          scoped, no-captcha send + the ordinary email verify live behind the
          visitor boundary. */}
      {codeKey ? <LockedEmailLogin codeKey={codeKey} /> : null}
    </>
  )
}
